import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from '../context/I18nContext';
import api from "../services/api";

/**
 * Caixa de entrada de Instagram e Messenger.
 *
 * A tela le do NOSSO banco, nao da Graph API: o que aparece aqui chegou por
 * webhook. E o loop que o video do App Review precisa mostrar - mensagem
 * entrando sozinha, resposta saindo pela UI e aparecendo no app nativo.
 *
 * O tempo real vem por SSE. O EventSource nao envia cabecalho customizado,
 * entao o JWT vai na query string.
 */

const BASE = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const rotuloPlataforma = {
  instagram: "Instagram Direct",
  facebook: "Facebook Messenger"
};

function horaCurta(valor) {
  if (!valor) return "";
  const d = new Date(valor);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function MetaInbox() {
  const { t } = useI18n();
  const [conexoes, setConexoes] = useState([]);
  const [conexaoId, setConexaoId] = useState(null);
  const [conversas, setConversas] = useState([]);
  const [conversaAberta, setConversaAberta] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  // Human Agent: a tag so vale para resposta digitada por uma pessoa.
  const [comoHumano, setComoHumano] = useState(false);
  const [aoVivo, setAoVivo] = useState(false);
  const [erro, setErro] = useState("");

  const fimDaLista = useRef(null);
  const conversaAbertaRef = useRef(null);
  conversaAbertaRef.current = conversaAberta;

  const conexao = useMemo(
    () => conexoes.find((c) => c.id === conexaoId) || null,
    [conexoes, conexaoId]
  );

  // ─── Carregamento ───────────────────────────────────────

  useEffect(() => {
    api
      .get("/meta-connections")
      .then((r) => {
        const lista = (r.data.connections || r.data || []).filter(
          (c) => c.platform === "instagram" || c.platform === "facebook"
        );
        setConexoes(lista);
        if (lista.length > 0) setConexaoId(lista[0].id);
      })
      .catch(() => setErro(t('inbox.errorConnections')));
  }, []);

  const carregarConversas = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/meta-connections/${id}/conversas`);
      setConversas(r.data.conversas || []);
    } catch (e) {
      setErro(t('inbox.errorConversations'));
    }
  }, []);

  const abrirConversa = useCallback(async (id, conversaId) => {
    setConversaAberta(conversaId);
    try {
      const r = await api.get(`/meta-connections/${id}/conversas/${encodeURIComponent(conversaId)}`);
      setEventos(r.data.eventos || []);
    } catch (e) {
      setErro(t('inbox.errorOpen'));
    }
  }, []);

  useEffect(() => {
    if (!conexaoId) return;
    setConversas([]);
    setEventos([]);
    setConversaAberta(null);
    carregarConversas(conexaoId);
  }, [conexaoId, carregarConversas]);

  // ─── Tempo real (SSE) ───────────────────────────────────

  useEffect(() => {
    if (!conexaoId) return undefined;

    const token = localStorage.getItem("@WhatsFlow:token");
    const fonte = new EventSource(
      t('metainbox.a1', { v1: BASE, v2: conexaoId, v3: encodeURIComponent(token || "") })
    );

    fonte.onopen = () => {
      setAoVivo(true);
      // Toda reconexao e um buraco: o EventSource volta sozinho, mas o que
      // chegou enquanto ele estava fora nao e reenviado. Como o banco e a
      // fonte de verdade, recarregar cobre o intervalo.
      carregarConversas(conexaoId);
      if (conversaAbertaRef.current) {
        abrirConversa(conexaoId, conversaAbertaRef.current);
      }
    };

    fonte.onerror = () => setAoVivo(false);

    fonte.addEventListener("evento", (e) => {
      let evento;
      try {
        evento = JSON.parse(e.data);
      } catch (err) {
        return;
      }

      // Se a thread aberta e a do evento, empurra na hora.
      if (evento.conversaId === conversaAbertaRef.current) {
        setEventos((atuais) =>
          atuais.some((x) => x.id === evento.id) ? atuais : [...atuais, evento]
        );
      }

      // A lista da esquerda tambem precisa refletir a chegada.
      setConversas((atuais) => {
        const resto = atuais.filter((c) => c.conversaId !== evento.conversaId);
        const anterior = atuais.find((c) => c.conversaId === evento.conversaId);
        return [
          {
            conversaId: evento.conversaId,
            tipo: evento.tipo,
            plataforma: evento.plataforma,
            direcao: evento.direcao,
            remetenteId: evento.remetenteId,
            remetenteNome: evento.remetenteNome,
            texto: evento.texto,
            ocorridoEm: evento.ocorridoEm,
            total: (anterior ? anterior.total : 0) + 1
          },
          ...resto
        ];
      });
    });

    return () => fonte.close();
  }, [conexaoId, carregarConversas, abrirConversa]);

  useEffect(() => {
    if (fimDaLista.current) fimDaLista.current.scrollIntoView({ behavior: "smooth" });
  }, [eventos]);

  // ─── Resposta ───────────────────────────────────────────

  const eventoDaConversa = eventos.length > 0 ? eventos[eventos.length - 1] : null;
  const ehComentario = eventoDaConversa && eventoDaConversa.tipo === "comentario";

  async function responder(e) {
    e.preventDefault();
    if (!texto.trim() || !conversaAberta || enviando) return;

    setEnviando(true);
    setErro("");
    try {
      if (ehComentario) {
        // Responder comentario e responder O COMENTARIO, nao a midia.
        const alvo = [...eventos].reverse().find((x) => x.tipo === "comentario" && x.direcao === "entrada");
        await api.post(
          `/meta-connections/${conexaoId}/comentarios/${encodeURIComponent(alvo ? alvo.idExterno : conversaAberta)}/responder`,
          { texto, conversaId: conversaAberta }
        );
      } else {
        await api.post(`/meta-connections/${conexaoId}/responder`, {
          conversaId: conversaAberta,
          texto,
          comoHumano
        });
      }
      setTexto("");
      setComoHumano(false);
      // O evento enviado volta pelo SSE (publicado na gravacao). Recarregar
      // aqui so cobriria o caso do Redis fora do ar.
      abrirConversa(conexaoId, conversaAberta);
    } catch (err) {
      const e2 = err.response && err.response.data && err.response.data.error;
      setErro((e2 && e2.message) || t('metainbox.a2'));
    } finally {
      setEnviando(false);
    }
  }

  // ─── Render ─────────────────────────────────────────────

  if (conexoes.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('inbox.title')}</h1>
        <p className="text-gray-600">{t('metainbox.j1')}<span className="font-medium"> Meta </span>{t('metainbox.j2')}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('inbox.title')}</h1>
          {conexao && (
            // Nome da conta conectada visivel: criterio de aceite do App Review.
            <p className="text-sm text-gray-600">
              {rotuloPlataforma[conexao.platform] || conexao.platform} ·{" "}
              <span className="font-medium">{conexao.pageName || conexao.name}</span>
              {conexao.instagramId ? t('inbox.idLabel', { v1: conexao.instagramId }) : ""}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full ${
              aoVivo ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${aoVivo ? "bg-green-500" : "bg-gray-400"}`} />
            {aoVivo ? t('metainbox.a3') : t('metainbox.a4')}
          </span>

          <select
            value={conexaoId || ""}
            onChange={(e) => setConexaoId(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {conexoes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({rotuloPlataforma[c.platform] || c.platform})
              </option>
            ))}
          </select>
        </div>
      </div>

      {erro && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{erro}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Conversas */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">
            {t('inbox.chatsCount', { v1: conversas.length })}
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
            {conversas.length === 0 && (
              <p className="p-4 text-sm text-gray-500">{t('metainbox.j3')}</p>
            )}
            {conversas.map((c) => (
              <button
                key={c.conversaId}
                onClick={() => abrirConversa(conexaoId, c.conversaId)}
                className={`w-full text-left p-4 hover:bg-gray-50 ${
                  conversaAberta === c.conversaId ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex justify-between items-baseline gap-2">
                  <span className="font-medium text-gray-900 truncate">
                    {c.remetenteNome || c.remetenteId || c.conversaId}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">{horaCurta(c.ocorridoEm)}</span>
                </div>
                <p className="text-sm text-gray-600 truncate">{c.texto || "(sem texto)"}</p>
                <span className="text-xs text-gray-400">
                  {c.tipo === "comentario" ? t('metainbox.a5') : t('metainbox.a6')} · {c.total}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 flex flex-col">
          {!conversaAberta ? (
            <p className="p-6 text-sm text-gray-500">{t('inbox.pickConversation')}</p>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">
                {ehComentario ? t('metainbox.a7') : t('metainbox.a8')} · {conversaAberta}
              </div>

              <div className="flex-1 max-h-[50vh] overflow-y-auto p-4 space-y-3">
                {eventos.map((ev) => (
                  <div
                    key={ev.id}
                    className={`flex ${ev.direcao === "saida" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        ev.direcao === "saida"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-900"
                      }`}
                    >
                      <p className="text-xs opacity-75 mb-1">
                        {ev.direcao === "saida" ? t('metainbox.a9') : ev.remetenteNome || ev.remetenteId}
                        {" · "}
                        {horaCurta(ev.ocorridoEm)}
                      </p>
                      <p className="whitespace-pre-wrap break-words">{ev.texto || "(sem texto)"}</p>
                    </div>
                  </div>
                ))}
                <div ref={fimDaLista} />
              </div>

              <form onSubmit={responder} className="p-4 border-t border-gray-200 flex gap-2">
                <input
                  type="text"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={ehComentario ? t('metainbox.a10') : t('metainbox.a11')}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                />
                <button
                  type="submit"
                  disabled={enviando || !texto.trim()}
                  className="px-5 py-2 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-50"
                >
                  {enviando ? t('metainbox.a12') : t('metainbox.a13')}
                </button>
              </form>
              {!ehComentario && (
                <label
                  title={t('ha.hint')}
                  className="flex items-center gap-2 px-4 pb-4 -mt-2 text-sm text-gray-600 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={comoHumano}
                    onChange={(e) => setComoHumano(e.target.checked)}
                    className="w-4 h-4 accent-blue-600"
                  />
                  {t('ha.label')}
                </label>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
